import React, { useState, useEffect, useMemo } from 'react';
import { getMilkIntake, getSuppliers, getSilos, createMilkIntake } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Loader2, Droplets, CheckCircle2, XCircle } from 'lucide-react';

const empty = {
  intake_date: new Date().toISOString().slice(0, 10),
  supplier_id: '', supplier_milk_ref: '', volume_litres: 0,
  collection_date: '', tanker_ref: '', silo_id: '',
  temperature_c: '', fat_pct: '', protein_pct: '',
  antibiotic_result: 'pass', accept_status: 'accepted',
  recorded_by: '', notes: '',
};

export default function MilkIntake() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [silos, setSilos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    setLoading(true);
    try {
      const [i, s, sl] = await Promise.all([getMilkIntake(), getSuppliers(), getSilos()]);
      setItems(i.data); setSuppliers(s.data); setSilos(sl.data);
    } catch (e) { toast.error('Failed to load milk intake'); }
    finally { setLoading(false); }
  })(); }, []);

  const supName = (id) => suppliers.find(s => s.id === id)?.name || '—';
  const siloName = (id) => silos.find(s => s.id === id)?.name || '—';
  const milkSuppliers = suppliers.filter(s => s.supplier_type === 'milk');

  const filtered = useMemo(() => items.filter(it => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (it.intake_ref || '').toLowerCase().includes(q)
      || (it.supplier_milk_ref || '').toLowerCase().includes(q)
      || (it.tanker_ref || '').toLowerCase().includes(q);
  }), [items, search]);

  const totalLitres = filtered.reduce((sum, it) => sum + (it.volume_litres || 0), 0);

  const save = async () => {
    if (!form.supplier_id || !form.volume_litres) { toast.error('Supplier and volume are required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        volume_litres: Number(form.volume_litres),
        temperature_c: form.temperature_c ? Number(form.temperature_c) : undefined,
        fat_pct: form.fat_pct ? Number(form.fat_pct) : undefined,
        protein_pct: form.protein_pct ? Number(form.protein_pct) : undefined,
        silo_id: form.silo_id || undefined,
        collection_date: form.collection_date || undefined,
      };
      await createMilkIntake(payload);
      toast.success('Milk intake recorded');
      setDialog(false);
      const r = await getMilkIntake(); setItems(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="milk-intake-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><Droplets className="w-5 h-5" />Milk Intake</h1>
          <p className="text-sm text-muted-foreground">Inwards · raw milk arriving at the factory. Antibiotic pass/fail and silo destination are the only required gates.</p>
        </div>
        <Button onClick={() => { setForm({ ...empty }); setDialog(true); }} data-testid="btn-new-intake"><Plus className="w-4 h-4 mr-1" />Record intake</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card><CardContent className="pt-5"><div className="text-xs text-muted-foreground">Total intakes</div><div className="text-2xl font-semibold">{filtered.length}</div></CardContent></Card>
        <Card><CardContent className="pt-5"><div className="text-xs text-muted-foreground">Total volume</div><div className="text-2xl font-semibold">{totalLitres.toLocaleString()} L</div></CardContent></Card>
        <Card><CardContent className="pt-5"><div className="text-xs text-muted-foreground">Suppliers used</div><div className="text-2xl font-semibold">{new Set(filtered.map(f => f.supplier_id)).size}</div></CardContent></Card>
      </div>

      <Card><CardContent className="pt-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search intake ref, supplier ref, tanker…" className="pl-9" />
        </div>
      </CardContent></Card>

      {loading ? <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> :
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr><th className="text-left px-4 py-3">Intake</th><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Supplier</th><th className="text-left px-4 py-3">Volume</th><th className="text-left px-4 py-3">Silo</th><th className="text-left px-4 py-3">Fat / Protein</th><th className="text-left px-4 py-3">Antibiotic</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">No intake records yet.</td></tr> :
                filtered.map(it => (
                  <tr key={it.id} className="border-t hover:bg-zinc-50">
                    <td className="px-4 py-3 font-mono text-xs">{it.intake_ref}</td>
                    <td className="px-4 py-3">{it.intake_date}</td>
                    <td className="px-4 py-3">{supName(it.supplier_id)}</td>
                    <td className="px-4 py-3 font-medium">{it.volume_litres?.toLocaleString()} L</td>
                    <td className="px-4 py-3">{siloName(it.silo_id)}</td>
                    <td className="px-4 py-3 text-xs">{it.fat_pct ? `${it.fat_pct}%` : '—'} / {it.protein_pct ? `${it.protein_pct}%` : '—'}</td>
                    <td className="px-4 py-3">
                      {it.antibiotic_result === 'pass' ? <Badge className="bg-emerald-100 text-emerald-700 border-0"><CheckCircle2 className="w-3 h-3 mr-1" />Pass</Badge> :
                       it.antibiotic_result === 'fail' ? <Badge className="bg-red-100 text-red-700 border-0"><XCircle className="w-3 h-3 mr-1" />Fail</Badge> :
                       <Badge variant="outline">N/R</Badge>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </CardContent></Card>}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Record milk intake</DialogTitle>
            <DialogDescription>Every litre of milk needs to be tied to a supplier, silo and antibiotic result. Keystone auto-records the silo movement.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-2">
            <div><Label>Intake date *</Label><Input type="date" value={form.intake_date} onChange={e => setForm({ ...form, intake_date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Supplier *</Label>
              <Select value={form.supplier_id} onValueChange={v => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger><SelectValue placeholder="Milk supplier…" /></SelectTrigger>
                <SelectContent>{milkSuppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Volume (litres) *</Label><Input type="number" value={form.volume_litres} onChange={e => setForm({ ...form, volume_litres: e.target.value })} /></div>
            <div><Label>Tanker ref</Label><Input value={form.tanker_ref} onChange={e => setForm({ ...form, tanker_ref: e.target.value })} /></div>
            <div><Label>Supplier milk ref</Label><Input value={form.supplier_milk_ref} onChange={e => setForm({ ...form, supplier_milk_ref: e.target.value })} /></div>
            <div><Label>Collection date</Label><Input type="date" value={form.collection_date} onChange={e => setForm({ ...form, collection_date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Silo</Label>
              <Select value={form.silo_id} onValueChange={v => setForm({ ...form, silo_id: v })}>
                <SelectTrigger><SelectValue placeholder="Which silo…" /></SelectTrigger>
                <SelectContent>{silos.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Temperature °C</Label><Input type="number" step="0.1" value={form.temperature_c} onChange={e => setForm({ ...form, temperature_c: e.target.value })} /></div>
            <div><Label>Fat %</Label><Input type="number" step="0.01" value={form.fat_pct} onChange={e => setForm({ ...form, fat_pct: e.target.value })} /></div>
            <div><Label>Protein %</Label><Input type="number" step="0.01" value={form.protein_pct} onChange={e => setForm({ ...form, protein_pct: e.target.value })} /></div>
            <div><Label>Antibiotic *</Label>
              <Select value={form.antibiotic_result} onValueChange={v => setForm({ ...form, antibiotic_result: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pass">Pass</SelectItem>
                  <SelectItem value="fail">Fail</SelectItem>
                  <SelectItem value="not_recorded">Not recorded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Accepted?</Label>
              <Select value={form.accept_status} onValueChange={v => setForm({ ...form, accept_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Recorded by</Label><Input value={form.recorded_by} onChange={e => setForm({ ...form, recorded_by: e.target.value })} /></div>
            <div className="col-span-3"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
